/* eslint-disable no-use-before-define */
import React from 'react';
import TextInput from './TextInput';
import RadioButton from './RadioButtonInput';
import SelectInput from './SelectInput';
import PhoneInput from './PhoneInput';
import countries from '../../utils/countries';
import { getText } from '../../utils/functions';
import CheckboxInput from './CheckboxInput';
import TextAreaInput from './TextAreaInput';
import { defaultConfig } from '../formDefinition/DefaultConfig';

const getInputComponent = type => {
	const inputTypes = {
		select: SelectInputComponent,
		phone: PhoneInputComponent,
		radio: RadioInputComponent,
		checkbox: CheckboxInputComponent,
		textarea: TextAreaInputComponent,
		text: TextInputComponent,
	};

	return inputTypes[type] || inputTypes.text;
};


function InputFactory({
	inputConfig,
	formData,
	setFormData,
	translate,
	value = '',
	/* OPTIONAL INPUTS */
	// portal,
	confirm = true,
	personType = 'person',
	showLabel = true,
	inputProps = {},
	colors
} = {}) {
	/* Variable to control value */
	const [valueConfirm, setValueConfirm] = React.useState();

	const representative = 'representative';
	const delegate = 'delegate';

	/* onChange functions */
	const onChange = val => {
		/* Representative */
		if (personType === representative) {
			return setFormData({
				...formData,
				representative: inputConfig?.type === 'radio' && val !== '' ?
					{
						name: val,
						surname: '',
						dni: '',
						email: '',
						phone: '',
						[inputConfig.name]: val
					}
					:
					{
						...formData?.representative,
						[inputConfig.name]: val
					}
			});
		}
		/* Delegate */
		if (personType === delegate) {
			return setFormData({
				...formData,
				delegate: inputConfig?.type === 'radio' && val !== '' ?
					{
						name: val,
						surname: '',
						dni: '',
						email: '',
						phone: '',
						[inputConfig.name]: val
					}
					:
					{
						...formData?.delegate,
						[inputConfig.name]: val
					}
			});
		}
		/* Person or entity */
		return setFormData({
			...formData,
			[inputConfig.name]: val
		});
	};

	const commonProps = {
		showLabel,
		value,
		onChange: val => onChange(val),
		inputProps,
		colors,
		translate,
		inputConfig
	};

	const confirmProps = {
		confirm: true,
		name: `confirm_${inputConfig.name}`,
		value: valueConfirm,
		onChange: val => setValueConfirm(val),
		confirmValue: value,
	};

	const InputComponent = getInputComponent(inputConfig.type);
	return <React.Fragment>
		<InputComponent { ...commonProps } />

		{/* CONFIRM OPTION */}
		{confirm && inputConfig?.confirm
			&& <InputComponent { ...commonProps } { ...confirmProps } />}
	</React.Fragment>;
}

export default InputFactory;

function SelectInputComponent({
	/* LABEL PROPS */
	showLabel,
	confirm,

	/* INPUT PROPS */
	name,
	value,
	onChange,
	required,
	checkbbdd,
	confirmValue,
	inputProps,

	/* COMMON PROPS */
	portal,
	inputConfig,
	translate,
	colors,
} = {}) {
	return <LabelWrapper
		showLabel={showLabel}
		inputConfig={inputConfig}
		translate={translate}
		colors={colors}
		confirm={confirm}
	>
		<SelectInput
			portal={portal}
			name={name ?? inputConfig.name}
			items={inputConfig.items}
			value={value}
			onChange={val => onChange(val)}
			translate={translate}
			required={required ?? inputConfig.required}
			checkbbdd={checkbbdd ?? inputConfig.checkbbdd}
			confirmValue={confirmValue}

			// InputFactory controlled variables
			colors={colors}

			// Styling options
			placeholder={inputProps?.placeholder}
			classNameDIV={inputProps?.classNameDIV}
			classNameBTN={inputProps?.classNameBTN}
			classNameITEMLIST={inputProps?.classNameITEMLIST}
			transition={inputProps?.transition}
			classNameOPTS={inputProps?.classNameOPTS}
			classNameITEM={inputProps?.classNameITEM}
		/>
	</LabelWrapper>;
}

function PhoneInputComponent({
	/* LABEL PROPS */
	showLabel,
	confirm,

	/* INPUT PROPS */
	name,
	value,
	onChange,
	required,
	checkbbdd,
	confirmValue,
	inputProps,

	/* COMMON PROPS */
	inputConfig,
	translate,
	colors,
} = {}) {
	// Array of country prefixes
	const prefixes = countries();

	return <LabelWrapper
		showLabel={showLabel}
		inputConfig={inputConfig}
		translate={translate}
		colors={colors}
		confirm={confirm}
	>
		<PhoneInput
			name={name ?? inputConfig.name}
			value={value}
			onChange={val => onChange(val)}
			translate={translate}
			required={required ?? inputConfig.required}
			checkbbdd={checkbbdd ?? inputConfig.checkbbdd}
			confirmValue={confirmValue}
			confirmError={inputProps?.confirmError}
			placeholderSearchBar={
				inputConfig?.placeholderSearchBarText?.[translate.currentLanguage]
				|| translate?.[inputConfig?.placeholderSearchBarLabel]
				|| inputConfig?.placeholderSearchBarLabel}
			defaultDialCode={inputProps?.defaultDialCode}

			dialCodeItems={prefixes}
			colors={colors}

			id={inputProps?.id}
			autoComplete={inputProps?.autoComplete}
			classNameDIV={inputProps?.classNameDIV}
			classNameLISTBOX={inputProps?.classNameLISTBOX}
			classNameBTN={inputProps?.classNameBTN}
			transition={inputProps?.transition}
			classNameOPTS={inputProps?.classNameOPTS}
			classNameSEARCH={inputProps?.classNameSEARCH}
			classNameITEMLIST={inputProps?.classNameITEMLIST}
			classNameITEM={inputProps?.classNameITEM}
			classNameNUMBER={inputProps?.classNameNUMBER}
		/>
	</LabelWrapper>;
}

function RadioInputComponent({
	/* LABEL PROPS */
	showLabel,
	confirm,

	/* INPUT PROPS */
	name,
	value,
	onChange,
	required,
	checkbbdd,
	confirmValue,
	inputProps,

	/* COMMON PROPS */
	inputConfig,
	translate,
	colors,
} = {}) {
	return <LabelWrapper
		showLabel={showLabel}
		inputConfig={inputConfig}
		translate={translate}
		colors={colors}
		confirm={confirm}
	>
		<RadioButton
			name={name ?? inputConfig.name}
			items={inputConfig.items}
			value={value}
			onChange={val => onChange(val)}
			translate={translate}
			required={required ?? inputConfig.required}
			checkbbdd={checkbbdd ?? inputConfig.checkbbdd}
			confirmValue={confirmValue}
			showValidation={inputConfig?.showValidation}
			lastDefault={inputConfig?.lastDefault}

			colors={colors}

			id={inputProps?.id}
			classNameOPTS={inputProps?.classNameOPTS}
			classNameITEM={inputProps?.classNameITEM}
		/>
	</LabelWrapper>;
}

function CheckboxInputComponent({
	/* LABEL PROPS */
	showLabel,
	confirm,

	/* INPUT PROPS */
	name,
	value,
	onChange,
	required,
	checkbbdd,
	confirmValue,
	inputProps,

	/* COMMON PROPS */
	inputConfig,
	translate,
	colors,
} = {}) {
	return <LabelWrapper
		showLabel={showLabel}
		inputConfig={inputConfig}
		translate={translate}
		colors={colors}
		confirm={confirm}
	>
		<CheckboxInput
			name={name ?? inputConfig.name}
			items={inputConfig.items}
			value={value}
			onChange={val => onChange(val)}
			translate={translate}
			required={required ?? inputConfig.required}
			checkbbdd={checkbbdd ?? inputConfig.checkbbdd}
			confirmValue={confirmValue}
			showValidation={inputConfig?.showValidation}

			colors={colors}

			id={inputProps?.id}
			maxSelections={inputConfig?.maxSelections}
			minSelections={inputConfig?.minSelections}
			uniqueValues={inputConfig?.uniqueValues}
		/>
	</LabelWrapper>;
}

function TextAreaInputComponent({
	/* LABEL PROPS */
	showLabel,
	confirm,

	/* INPUT PROPS */
	name,
	value,
	onChange,
	required,
	checkbbdd,
	confirmValue,
	inputProps,

	/* COMMON PROPS */
	inputConfig,
	translate,
	colors,
} = {}) {
	return <LabelWrapper
		showLabel={showLabel}
		inputConfig={inputConfig}
		translate={translate}
		colors={colors}
		confirm={confirm}
	>
		<TextAreaInput
			name={name ?? inputConfig.name}
			value={value}
			onChange={val => onChange(val)}
			translate={translate}
			required={required ?? inputConfig.required}
			checkbbdd={checkbbdd ?? inputConfig.checkbbdd}
			confirmValue={confirmValue}
			maxSize={inputConfig.maxSize}
			colors={colors}

			id={inputProps?.id} />
	</LabelWrapper>;
}

function TextInputComponent({
	/* LABEL PROPS */
	showLabel,
	confirm,

	/* INPUT PROPS */
	name,
	value,
	onChange,
	required,
	checkbbdd,
	confirmValue,
	inputProps,

	/* COMMON PROPS */
	inputConfig,
	translate,
	colors,
} = {}) {
	return <LabelWrapper
		showLabel={showLabel}
		inputConfig={inputConfig}
		translate={translate}
		colors={colors}
		confirm={confirm}
	>
		<TextInput
			name={name ?? inputConfig.name}
			value={value}
			onChange={val => onChange(val)}
			translate={translate}
			required={required ?? inputConfig.required}
			checkbbdd={checkbbdd ?? inputConfig.checkbbdd}
			confirmValue={confirmValue}
			confirmError={getText(defaultConfig?.validation?.confirmError, translate)}

			colors={colors}

			id={inputProps?.id} />
	</LabelWrapper>;
}

function LabelWrapper({
	children,

	showLabel,
	inputConfig,
	translate,
	colors,
	confirm,

	confirmTag,
	classNameDIV = 'relative flex flex-col space-y-0 w-full',
	classNameLBL = 'w-max text-xs md:text-sm',
	classNameRequired = 'absolute z-50 top-5 md:top-6 ml-0.5 p-1 text-xs md:text-sm',
} = {}) {
	/* Variable that checks if any point of the component is being hovered so style can be applied to the Box */
	const [hover, setHover] = React.useState(false);
	/* Mouseover Styling */
	const onMouseOver = () => setHover(true);
	const onMouseLeave = () => setHover(false);

	/* Default values for the styles */
	const defaultStyleDIV = {
		color: colors?.text,
		transition: 'all 300ms ease-in'
	};
	const defaultStyleRequired = {
		color: colors?.text,
		backgroundColor: colors?.light,
		transition: 'all 300ms ease-in'
	};

	return <div
		className={classNameDIV}
		style={defaultStyleDIV}
	>
		{showLabel
			&& <label
				className={classNameLBL}
				onMouseOver={onMouseOver}
				onMouseLeave={onMouseLeave}
			>
				{confirm ?
					`${confirmTag || translate?.confirm} ${getText(inputConfig, translate)} ${inputConfig?.required ? '*' : ''}`
					: `${getText(inputConfig, translate)} ${inputConfig?.required ? '*' : ''}`
				}
			</label>}
		{inputConfig?.required && hover
			&& <label
				className={classNameRequired}
				style={defaultStyleRequired}
			>
				{translate.field_required}
			</label>}
		{children}
	</div>;
}
